const DEFAULT_RATING = 4;
const RATING_CONFIDENCE_REVIEWS = 20;

const toFiniteNumber = (value, fallback = null) => {
  if (
    value === null ||
    value === undefined ||
    (typeof value === "string" && !value.trim())
  ) {
    return fallback;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const clamp = (value, minimum, maximum) =>
  Math.min(maximum, Math.max(minimum, value));

export const normalizeWeCity = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();

const singularizeSearchToken = (token) => {
  if (token.length > 5 && token.endsWith("es")) return token.slice(0, -2);
  if (token.length > 4 && token.endsWith("s")) return token.slice(0, -1);
  return token;
};

export const matchesWeSearch = (value, search) => {
  const normalizedValue = normalizeWeCity(value);
  const queryTokens = normalizeWeCity(search).split(" ").filter(Boolean);
  if (!queryTokens.length) return true;
  const valueTokens = normalizedValue.split(" ").filter(Boolean);

  return queryTokens.every((queryToken) => {
    if (normalizedValue.includes(queryToken)) return true;
    const singularQuery = singularizeSearchToken(queryToken);
    return valueTokens.some(
      (valueToken) =>
        singularizeSearchToken(valueToken).includes(singularQuery)
    );
  });
};

export const calculateWeDistanceKm = (
  originLatitude,
  originLongitude,
  destinationLatitude,
  destinationLongitude
) => {
  const originLat = toFiniteNumber(originLatitude);
  const originLng = toFiniteNumber(originLongitude);
  const destinationLat = toFiniteNumber(destinationLatitude);
  const destinationLng = toFiniteNumber(destinationLongitude);

  if (
    originLat === null ||
    originLng === null ||
    destinationLat === null ||
    destinationLng === null
  ) {
    return null;
  }

  const toRadians = (degrees) => (degrees * Math.PI) / 180;
  const earthRadiusKm = 6371;
  const latitudeDistance = toRadians(destinationLat - originLat);
  const longitudeDistance = toRadians(destinationLng - originLng);
  const startLatitude = toRadians(originLat);
  const endLatitude = toRadians(destinationLat);
  const haversine =
    Math.sin(latitudeDistance / 2) ** 2 +
    Math.cos(startLatitude) *
      Math.cos(endLatitude) *
      Math.sin(longitudeDistance / 2) ** 2;

  return Math.round(
    earthRadiusKm * 2 * Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine)) * 10
  ) / 10;
};

export const calculateWeRestaurantScore = (restaurant = {}) => {
  const rating = clamp(toFiniteNumber(restaurant.ratingAverage, DEFAULT_RATING), 0, 5);
  const reviewCount = Math.max(0, toFiniteNumber(restaurant.ratingCount, 0));
  const weightedRating =
    (reviewCount / (reviewCount + RATING_CONFIDENCE_REVIEWS)) * rating +
    (RATING_CONFIDENCE_REVIEWS /
      (reviewCount + RATING_CONFIDENCE_REVIEWS)) *
      DEFAULT_RATING;
  const completedOrders = Math.max(
    0,
    toFiniteNumber(restaurant.completedOrders, 0)
  );
  const completionConfidence = Math.min(1, Math.log10(completedOrders + 1) / 3);
  const onTimeRate = clamp(toFiniteNumber(restaurant.onTimeRate, 0.85), 0, 1);
  const cancellationRate = clamp(
    toFiniteNumber(restaurant.cancellationRate, 0),
    0,
    1
  );
  const availabilityScore = restaurant.isOpen && restaurant.acceptingOrders ? 1 : 0;

  return Math.round(
    (
      weightedRating * 0.64 +
      completionConfidence * 0.55 +
      onTimeRate * 0.45 +
      (1 - cancellationRate) * 0.36 +
      availabilityScore * 0.2
    ) * 1000
  ) / 1000;
};

export const prepareWeRestaurants = ({
  restaurants = [],
  city = "",
  latitude = null,
  longitude = null,
} = {}) => {
  const normalizedCity = normalizeWeCity(city);

  return (Array.isArray(restaurants) ? restaurants : [])
    .map((restaurant) => {
      const distanceKm =
        toFiniteNumber(restaurant.distanceKm) ??
        calculateWeDistanceKm(
          latitude,
          longitude,
          restaurant.latitude,
          restaurant.longitude
        );
      const normalizedRestaurant = {
        ...restaurant,
        distanceKm,
        ratingAverage: clamp(
          toFiniteNumber(restaurant.ratingAverage, DEFAULT_RATING),
          0,
          5
        ),
        ratingCount: Math.max(0, toFiniteNumber(restaurant.ratingCount, 0)),
        completedOrders: Math.max(
          0,
          toFiniteNumber(restaurant.completedOrders, 0)
        ),
        onTimeRate: clamp(toFiniteNumber(restaurant.onTimeRate, 0.85), 0, 1),
        cancellationRate: clamp(
          toFiniteNumber(restaurant.cancellationRate, 0),
          0,
          1
        ),
        acceptingOrders: restaurant.acceptingOrders !== false,
        isOpen: restaurant.isOpen !== false,
        foodTypes: Array.isArray(restaurant.foodTypes)
          ? restaurant.foodTypes.filter(Boolean)
          : [],
        paymentMethods: Array.isArray(restaurant.paymentMethods)
          ? restaurant.paymentMethods.filter(Boolean)
          : [],
        matchedProducts: Array.isArray(restaurant.matchedProducts)
          ? restaurant.matchedProducts
              .filter((product) => product?.id && product?.name)
              .map((product) => ({
                ...product,
                ratingAverage: clamp(
                  toFiniteNumber(product.ratingAverage, 0),
                  0,
                  5
                ),
                ratingCount: Math.max(
                  0,
                  toFiniteNumber(product.ratingCount, 0)
                ),
              }))
          : [],
      };

      return {
        ...normalizedRestaurant,
        relevanceScore: calculateWeRestaurantScore(normalizedRestaurant),
      };
    })
    .filter((restaurant) => {
      if (!restaurant.acceptingOrders) return false;
      if (!normalizedCity) return true;
      return normalizeWeCity(restaurant.city) === normalizedCity;
    });
};

export const filterWeRestaurants = (restaurants = [], filters = {}) => {
  const search = normalizeWeCity(filters.search);
  const foodType = normalizeWeCity(filters.foodType);
  const maxDistance = toFiniteNumber(filters.maxDistanceKm);
  const maxEta = toFiniteNumber(filters.maxEtaMinutes);
  const minimumRating = toFiniteNumber(filters.minimumRating);
  const priceLevel = toFiniteNumber(filters.priceLevel);
  const paymentMethod = normalizeWeCity(filters.paymentMethod);

  return restaurants.filter((restaurant) => {
    const searchableText = normalizeWeCity(
      [
        restaurant.name,
        restaurant.description,
        ...(restaurant.foodTypes || []),
        ...(restaurant.matchedProducts || []).flatMap((product) => [
          product.name,
          product.description,
          product.categoryName,
        ]),
      ].join(" ")
    );
    const normalizedFoodTypes = (restaurant.foodTypes || []).map(normalizeWeCity);
    const normalizedPaymentMethods = (restaurant.paymentMethods || []).map(
      normalizeWeCity
    );

    if (search && !matchesWeSearch(searchableText, search)) return false;
    if (foodType && !normalizedFoodTypes.includes(foodType)) return false;
    if (
      maxDistance !== null &&
      restaurant.distanceKm !== null &&
      restaurant.distanceKm > maxDistance
    ) {
      return false;
    }
    if (maxEta !== null && Number(restaurant.estimatedMinutes || 0) > maxEta) {
      return false;
    }
    if (minimumRating !== null && restaurant.ratingAverage < minimumRating) {
      return false;
    }
    if (priceLevel !== null && Number(restaurant.priceLevel || 0) !== priceLevel) {
      return false;
    }
    if (filters.promotionsOnly && Number(restaurant.promotionCount || 0) <= 0) {
      return false;
    }
    if (filters.freeDeliveryOnly && Number(restaurant.deliveryFee || 0) > 0) {
      return false;
    }
    if (paymentMethod && !normalizedPaymentMethods.includes(paymentMethod)) {
      return false;
    }

    return true;
  });
};

export const findWeProductMatches = (restaurants = [], search = "") => {
  const normalizedSearch = normalizeWeCity(search);
  if (!normalizedSearch) return [];

  return restaurants
    .flatMap((restaurant) =>
      (restaurant.matchedProducts || []).map((product) => ({
        ...product,
        restaurantId: restaurant.id,
        restaurantName: restaurant.name,
        restaurantLogoUrl: restaurant.logoUrl,
        restaurantIsOpen: restaurant.isOpen,
        menuUrl: restaurant.menuUrl,
      }))
    )
    .filter((product) =>
      matchesWeSearch(
        [product.name, product.description, product.categoryName].join(" "),
        normalizedSearch
      )
    )
    .sort((left, right) => {
      const leftCount = Math.max(0, Number(left.ratingCount || 0));
      const rightCount = Math.max(0, Number(right.ratingCount || 0));
      const leftScore =
        (Number(left.ratingAverage || 0) * leftCount + DEFAULT_RATING * 5) /
        (leftCount + 5);
      const rightScore =
        (Number(right.ratingAverage || 0) * rightCount + DEFAULT_RATING * 5) /
        (rightCount + 5);
      return (
        rightScore - leftScore ||
        rightCount - leftCount ||
        String(left.name || "").localeCompare(String(right.name || ""), "es")
      );
    });
};

export const buildWeRestaurantSections = (restaurants = []) => ({
  nearby: [...restaurants]
    .filter((restaurant) => restaurant.distanceKm !== null)
    .sort(
      (left, right) =>
        Number(right.isOpen) - Number(left.isOpen) ||
        left.distanceKm - right.distanceKm ||
        right.relevanceScore - left.relevanceScore
    ),
  bestRated: [...restaurants].sort(
    (left, right) =>
      Number(right.isOpen) - Number(left.isOpen) ||
      right.relevanceScore - left.relevanceScore ||
      right.ratingCount - left.ratingCount
  ),
  all: [...restaurants].sort(
    (left, right) =>
      Number(right.isOpen) - Number(left.isOpen) ||
      String(left.name || "").localeCompare(String(right.name || ""), "es")
  ),
});
